import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  GlobalSearchTab,
  SearchEntityType,
  Search,
  GlobalSearchViewMode,
} from "@/models/search/Search";
import { debounce } from "lodash";
import { useSearchParams } from "react-router-dom";
import useSearch from "@/hooks/useSearch";
import {
  SourceCfg,
  SourceDictionary,
} from "@/models/legislation/LegislationFilter";
import useSearchStore from "@/stores/search.store";
import {
  buildSearchUrlParams,
  parseSearchUrlParams,
} from "@/utils/searchParamsFormatter";
import { SearchResult } from "@/models/search/SearchResults";
import {
  SearchPagination,
  EntityPaginationInfo,
} from "@/models/search/SearchPagination";
import { LeveledFiltersConfig } from "@/components/data-table/data-table";
import { useLegislationFilterConfig } from "@/providers/legislation-filter/hooks/useLegislationFilterConfig";
import { useTenantSearchFilterState } from "@/providers/legislation-filter/hooks/useTenantSearchFilterState";
import { GlobalSearchContext } from "./GlobalSearchContext";
import { useLocation } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import { useClearSearchAfterClose } from "@/pages/global-search/hooks/useClearSearchAfterClose";
import { withClearedSearchText } from "@/pages/global-search/utils/searchUtils";
import {
  LEGISLATION_SOURCE_FILTER_KEY,
  WORKSPACE_DOCUMENT_FILTER_PREFIX,
} from "@/pages/global-search/constants/searchFilters";

interface GlobalSearchProviderProps {
  children: React.ReactNode;
  initialTab?: GlobalSearchTab;
}

export const GlobalSearchProvider = ({
  children,
  initialTab = GlobalSearchTab.All,
}: GlobalSearchProviderProps) => {
  // --------------HOOKS--------------
  const {
    searchQuery,
    setSearchQuery,
    setIsSearchbarOpen,
    setIsFilterOpen,
    isSearchbarOpen,
    isFilterOpen,
  } = useSearchStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const previousPathnameRef = useRef(location.pathname);
  const lastUrlParamsRef = useRef<string>("");
  const { useTenantSearch } = useSearch();
  // Get filter config and defaults from context
  const { filterConfig, defaultsPlan } = useLegislationFilterConfig();

  const {
    initializeDefaults,
    markDefaultsInitialized,
    hasInitializedDefaults,
  } = useTenantSearchFilterState();

  // --------------STATE--------------
  // Whether to update the URL with the current search query - for actual search page it is true, for search bar it is false
  const [updateUrl, setUpdateUrl] = useState(false);
  // Input value from search bar
  const [inputValue, setInputValue] = useState(searchQuery?.searchTerm || "");

  // New global search modal state - which category is expanded (null = overview mode)
  const [expandedCategory, setExpandedCategory] =
    useState<SearchEntityType | null>(null);

  // Modal-specific pagination for expanded view
  const [modalLegislationPage, setModalLegislationPage] = useState(1);
  const [modalDocumentPage, setModalDocumentPage] = useState(1);
  const [isLoadingModalMore, setIsLoadingModalMore] = useState(false);

  // Accumulated results for modal infinite scroll (typed as arrays)
  const [accumulatedLegislationResults, setAccumulatedLegislationResults] =
    useState<SearchResult["legislation"][]>([]);
  const [accumulatedDocumentResults, setAccumulatedDocumentResults] = useState<
    SearchResult["workspaceDocument"][]
  >([]);

  // Focused source (null = show all selected sources)
  const [focusedSource, setFocusedSource] = useState<string | null>(null);

  // For individual entity tabs - infinite scroll state
  const [tabPage, setTabPage] = useState<number>(1);
  const [tabResults, setTabResults] = useState<SearchResult[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [lastPaginationInfo, setLastPaginationInfo] = useState<{
    entityPaginationInfo: Record<string, EntityPaginationInfo>;
    totalRecords: number;
  } | null>(null);

  // --------------REFS--------------
  // Track if we're updating sources internally to avoid sync loops
  const isUpdatingSourcesRef = useRef(false);

  // Track if we've applied defaults internally to avoid applying them multiple times
  const hasAppliedDefaultsRef = useRef(false);

  // Initialize sources from searchQuery.filters (legislation source key)
  const [sources, setSources] = useState<string[]>(() => {
    const filters = searchQuery?.filters;
    const datasourceValue = filters?.[LEGISLATION_SOURCE_FILTER_KEY];
    if (datasourceValue) {
      return Array.isArray(datasourceValue)
        ? datasourceValue
        : [datasourceValue];
    }
    return [];
  });

  // Sync sources with searchQuery.filters when it changes externally
  useEffect(() => {
    // Skip sync if we're updating sources internally
    if (isUpdatingSourcesRef.current) {
      isUpdatingSourcesRef.current = false;
      return;
    }

    const filters = searchQuery?.filters;
    const datasourceValue = filters?.[LEGISLATION_SOURCE_FILTER_KEY];
    if (datasourceValue) {
      const newSources = Array.isArray(datasourceValue)
        ? datasourceValue
        : [datasourceValue];
      if (JSON.stringify(newSources) !== JSON.stringify(sources)) {
        setSources(newSources);
      }
    } else if (sources.length > 0) {
      setSources([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery?.filters?.[LEGISLATION_SOURCE_FILTER_KEY]]);

  const leveledFilters = useMemo<LeveledFiltersConfig | undefined>(() => {
    if (!filterConfig) return;
    const leveledSources =
      (filterConfig as SourceDictionary).sources ?? filterConfig;
    return {
      rootColumnId: LEGISLATION_SOURCE_FILTER_KEY,
      levels: leveledSources,
    };
  }, [filterConfig]);

  // Get current tab
  const currentTab = searchQuery?.tab || GlobalSearchTab.All;

  // Page size for all queries (both "All" tab and individual tabs)
  const pageSize = 20;

  // Helper to get entity types for a tab
  const getEntityTypes = useCallback(
    (tab: GlobalSearchTab): SearchEntityType[] => {
      switch (tab) {
        case GlobalSearchTab.Legislation:
          return [SearchEntityType.Legislation];
        case GlobalSearchTab.Workspace:
          return [SearchEntityType.Workspace];
        case GlobalSearchTab.WorkspaceDocuments:
          return [SearchEntityType.WorkspaceDocument];
        case GlobalSearchTab.Chat:
          return [SearchEntityType.Chat];
        case GlobalSearchTab.FavoriteSearches:
          return [];
        case GlobalSearchTab.All:
        default:
          return [
            SearchEntityType.Legislation,
            SearchEntityType.Workspace,
            SearchEntityType.WorkspaceDocument,
            SearchEntityType.Chat,
          ];
      }
    },
    []
  );

  // Build filters for a specific entity type (used for "All" tab parallel queries)
  const buildFiltersForEntityType = useCallback(
    (
      searchQuery: Search | null,
      entityKey: "legislation" | "workspace" | "workspaceDocument" | "chat"
    ): Record<string, string[]> => {
      if (entityKey === "legislation") {
        return searchQuery?.filters || {};
      }
      if (entityKey === "workspaceDocument") {
        return searchQuery?.workspaceDocumentFilters || {};
      }
      return {};
    },
    []
  );

  const buildFiltersObject = useCallback(
    (
      searchQuery: Search | null,
      currentSourcesOverride?: string[]
    ): Record<string, string[]> => {
      if (!searchQuery?.filters) return {};

      const currentTab = searchQuery.tab || GlobalSearchTab.All;
      const filters = searchQuery.filters;
      const currentSourcesForFilters = currentSourcesOverride ?? sources;

      // Legislation tab: use filters. WorkspaceDocuments tab: use workspaceDocumentFilters.
      if (currentTab === GlobalSearchTab.WorkspaceDocuments) {
        return searchQuery?.workspaceDocumentFilters || {};
      }
      if (
        currentTab !== GlobalSearchTab.Legislation &&
        currentTab !== GlobalSearchTab.All
      ) {
        return {};
      }

      // Process filters based on accessToAllFilter setting (only for Legislation tab)
      if (
        currentTab === GlobalSearchTab.Legislation &&
        currentSourcesForFilters.length === 1 &&
        filterConfig?.sources?.[currentSourcesForFilters[0]]
      ) {
        const selectedSource = currentSourcesForFilters[0];
        const sourceCfg = filterConfig.sources[selectedSource] as SourceCfg;

        // If accessToAllFilter is explicitly true, return filters as-is
        // If undefined, treat as false (limited access)
        // If false, process filters with all options
        if (sourceCfg.accessToAllFilter !== true) {
          // accessToAllFilter is false or undefined - need to handle empty/missing childFilters
          const processedFilters: Record<string, string[]> = { ...filters };

          // For each childFilter in config, use the namespaced key
          for (const [filterKey, configFilter] of Object.entries(
            sourceCfg.filters || {}
          )) {
            const namespacedKey = `${selectedSource}::${filterKey}`;
            const existingFilterValue = processedFilters[namespacedKey];

            // If filter doesn't exist or has empty array (0 selections)
            if (!existingFilterValue || existingFilterValue.length === 0) {
              // Add all options from configuration
              const allOptions = configFilter.options || [];
              if (allOptions.length > 0) {
                // Map options to proper format (handle both string and object options)
                const optionValues = allOptions.map((opt) => {
                  if (typeof opt === "string") return opt;
                  if (opt && typeof opt === "object" && "value" in opt) {
                    return String((opt as { value: unknown }).value);
                  }
                  if (
                    opt &&
                    typeof opt === "object" &&
                    "label" in opt &&
                    "value" in opt
                  ) {
                    return String((opt as { value: unknown }).value);
                  }
                  return String(opt ?? "");
                });
                processedFilters[namespacedKey] = optionValues;
              }
            }
          }

          return processedFilters;
        }
      }

      return filters;
    },
    [sources, filterConfig]
  );
  // When a source is "focused" (eye-off), we EXCLUDE it from results
  const effectiveSources = useMemo(
    () =>
      focusedSource ? sources.filter((s) => s !== focusedSource) : sources,
    [focusedSource, sources]
  );

  // Clear focused source if it is no longer selected
  useEffect(() => {
    if (focusedSource && !sources.includes(focusedSource)) {
      setFocusedSource(null);
    }
  }, [focusedSource, sources]);

  // Build source-specific filters for multi-source queries
  // Parses namespaced keys (e.g., "retsinformation::status") to group filters by source
  const buildSourceFilters = useCallback(
    (
      currentSources: string[],
      filters: Record<string, string[]> | undefined
    ): Record<string, Record<string, string[]>> | undefined => {
      if (!currentSources.length || !filters || !filterConfig?.sources) {
        return undefined;
      }

      const sourceFilters: Record<string, Record<string, string[]>> = {};

      // For each selected source, extract its namespaced filters
      for (const sourceKey of currentSources) {
        const sourceCfg = filterConfig.sources[sourceKey];
        if (!sourceCfg) continue;

        const sourceFilterKeys = Object.keys(sourceCfg.filters || {});
        const filtersForSource: Record<string, string[]> = {};

        for (const [compositeKey, filterValues] of Object.entries(filters)) {
          // Parse namespaced key: "retsinformation::status" → source="retsinformation", key="status"
          const sepIdx = compositeKey.indexOf("::");
          if (sepIdx !== -1) {
            const keySource = compositeKey.substring(0, sepIdx);
            const originalKey = compositeKey.substring(sepIdx + 2);
            if (
              keySource === sourceKey &&
              sourceFilterKeys.includes(originalKey) &&
              filterValues.length > 0
            ) {
              filtersForSource[originalKey] = filterValues;
            }
          } else if (compositeKey !== LEGISLATION_SOURCE_FILTER_KEY) {
            // Fallback: non-namespaced key (backward compat) — match by config membership
            if (
              sourceFilterKeys.includes(compositeKey) &&
              filterValues.length > 0
            ) {
              filtersForSource[compositeKey] = filterValues;
            }
          }
        }

        // Always include the source, even if it has no filters
        sourceFilters[sourceKey] = filtersForSource;
      }

      return Object.keys(sourceFilters).length > 0 ? sourceFilters : undefined;
    },
    [filterConfig]
  );

  const updateFilter = useCallback(
    (filterKey: string, values: string[]) => {
      setSearchQuery((prev) => {
        const prevQuery = prev ?? null;

        if (
          filterKey.startsWith(
            WORKSPACE_DOCUMENT_FILTER_PREFIX
          )
        ) {
          const wdKey = filterKey.slice(
            WORKSPACE_DOCUMENT_FILTER_PREFIX.length
          );
          const prevWdFilters = prevQuery?.workspaceDocumentFilters ?? {};
          const newWdFilters = { ...prevWdFilters };
          if (values.length > 0) {
            newWdFilters[wdKey] = values;
          } else {
            delete newWdFilters[wdKey];
          }
          return {
            ...(prevQuery || {}),
            workspaceDocumentFilters: newWdFilters,
          } as Search;
        }

        const prevFilters = prevQuery?.filters ?? {};
        const newFilters = { ...prevFilters };
        if (values.length > 0) {
          newFilters[filterKey] = values;
        } else {
          delete newFilters[filterKey];
        }
        return {
          ...(prevQuery || {}),
          filters: newFilters,
        } as Search;
      });

      // Reset pagination for individual tabs when filters change
      setTabResults([]);
      setTabPage(1);
    },
    [setSearchQuery]
  );

  // Helper to combine search term and tags
  const buildSearchTerm = useCallback(() => {
    const term = searchQuery?.searchTerm || "";
    const tags = Array.isArray(searchQuery?.searchTags)
      ? searchQuery.searchTags.join(" ")
      : "";
    return (term + " " + tags).trim();
  }, [searchQuery?.searchTerm, searchQuery?.searchTags]);

  // For "All" tab - 4 separate parallel queries
  // Use modalLegislationPage when expanded to support infinite scroll
  const { data: legislationData, isLoading: isLoadingLegislation } =
    useTenantSearch({
      searchTerm: buildSearchTerm(),
      entityTypes: [SearchEntityType.Legislation],
      pageNumber:
        expandedCategory === SearchEntityType.Legislation
          ? modalLegislationPage
          : 1,
      pageSize,
      sources: effectiveSources,
      // Use sourceFilters for proper per-source filter handling
      sourceFilters: buildSourceFilters(
        effectiveSources,
        buildFiltersForEntityType(searchQuery, "legislation")
      ),
      enabled:
        currentTab === GlobalSearchTab.All &&
        (!!searchQuery?.searchTerm || !!searchQuery?.searchTags?.length),
    });

  // Only run Workspace query on full search page (updateUrl=true), not in global search modal
  const { data: workspaceData, isLoading: isLoadingWorkspace } =
    useTenantSearch({
      searchTerm: buildSearchTerm(),
      entityTypes: [SearchEntityType.Workspace],
      pageNumber: 1,
      pageSize,
      filters: buildFiltersForEntityType(searchQuery, "workspace"),
      enabled:
        updateUrl && // Only on full search page
        currentTab === GlobalSearchTab.All &&
        (!!searchQuery?.searchTerm || !!searchQuery?.searchTags?.length),
    });

  // Use modalDocumentPage when expanded to support infinite scroll
  const { data: documentData, isLoading: isLoadingDocument } = useTenantSearch({
    searchTerm: buildSearchTerm(),
    entityTypes: [SearchEntityType.WorkspaceDocument],
    pageNumber:
      expandedCategory === SearchEntityType.WorkspaceDocument
        ? modalDocumentPage
        : 1,
    pageSize,
    filters: buildFiltersForEntityType(searchQuery, "workspaceDocument"),
    enabled:
      currentTab === GlobalSearchTab.All &&
      (!!searchQuery?.searchTerm || !!searchQuery?.searchTags?.length),
  });

  // Only run Chat query on full search page (updateUrl=true), not in global search modal
  const { data: chatData, isLoading: isLoadingChat } = useTenantSearch({
    searchTerm: buildSearchTerm(),
    entityTypes: [SearchEntityType.Chat],
    pageNumber: 1,
    pageSize,
    filters: buildFiltersForEntityType(searchQuery, "chat"),
    enabled:
      updateUrl && // Only on full search page
      currentTab === GlobalSearchTab.All &&
      (!!searchQuery?.searchTerm || !!searchQuery?.searchTags?.length),
  });

  // For individual entity tabs - single query with infinite scroll
  // Only run on full search page (updateUrl=true), not in global search modal
  const { data: singleTabData, isLoading: isLoadingSingleTab } =
    useTenantSearch({
      searchTerm: buildSearchTerm(),
      entityTypes:
        currentTab !== GlobalSearchTab.All
          ? getEntityTypes(currentTab)
          : undefined,
      pageNumber: tabPage,
      pageSize,
      sources: effectiveSources,
      // Use sourceFilters for Legislation tab, otherwise use flat filters
      sourceFilters:
        currentTab === GlobalSearchTab.Legislation
          ? buildSourceFilters(
              effectiveSources,
              buildFiltersObject(searchQuery, effectiveSources)
            )
          : undefined,
      filters:
        currentTab !== GlobalSearchTab.Legislation
          ? buildFiltersObject(searchQuery, effectiveSources)
          : undefined,
      enabled: updateUrl && currentTab !== GlobalSearchTab.All,
    });

  const loadMoreResults = useCallback(() => {
    // Only allow infinite loading for individual entity tabs, not "All" tab
    if (
      currentTab !== GlobalSearchTab.All &&
      !isLoadingSingleTab &&
      !isLoadingMore &&
      hasMoreData
    ) {
      setIsLoadingMore(true);
      setTabPage((prev) => prev + 1);
    }
  }, [currentTab, isLoadingSingleTab, isLoadingMore, hasMoreData]);

  // Extract typed results from "All" tab queries
  const legislationResults = useMemo(
    () =>
      legislationData?.data
        .map((r) => r.legislation)
        .filter((r): r is NonNullable<typeof r> => !!r),
    [legislationData]
  );

  const workspaceResults = useMemo(
    () =>
      workspaceData?.data
        .map((r) => r.workspace)
        .filter((r): r is NonNullable<typeof r> => !!r),
    [workspaceData]
  );

  const documentResults = useMemo(
    () =>
      documentData?.data
        .map((r) => r.workspaceDocument)
        .filter((r): r is NonNullable<typeof r> => !!r),
    [documentData]
  );

  const chatResults = useMemo(
    () =>
      chatData?.data
        .map((r) => r.chat)
        .filter((r): r is NonNullable<typeof r> => !!r),
    [chatData]
  );

  // Extract hasMore flags for each entity type
  // For modal expanded view, check against current page; otherwise just check first page
  const legislationHasMore = useMemo(() => {
    const totalRecords = legislationData?.totalRecords ?? 0;
    const currentPage =
      expandedCategory === SearchEntityType.Legislation
        ? modalLegislationPage
        : 1;
    return totalRecords > currentPage * pageSize;
  }, [legislationData, expandedCategory, modalLegislationPage, pageSize]);

  const workspaceHasMore = useMemo(
    () => (workspaceData?.totalRecords ?? 0) > pageSize,
    [workspaceData]
  );

  const documentHasMore = useMemo(() => {
    const totalRecords = documentData?.totalRecords ?? 0;
    const currentPage =
      expandedCategory === SearchEntityType.WorkspaceDocument
        ? modalDocumentPage
        : 1;
    return totalRecords > currentPage * pageSize;
  }, [documentData, expandedCategory, modalDocumentPage, pageSize]);

  const chatHasMore = useMemo(
    () => (chatData?.totalRecords ?? 0) > pageSize,
    [chatData]
  );

  // Extract total records for each entity type (for displaying actual counts)
  const legislationTotalRecords = useMemo(
    () => legislationData?.totalRecords ?? 0,
    [legislationData]
  );

  const documentTotalRecords = useMemo(
    () => documentData?.totalRecords ?? 0,
    [documentData]
  );

  // Extract source counts from legislation data (for multi-source queries)
  const sourceCounts = useMemo(
    () => legislationData?.sourceCounts,
    [legislationData]
  );

  // Load more results for modal expanded view (independent of tab system)
  const loadMoreModalResults = useCallback(() => {
    if (!expandedCategory || isLoadingModalMore) return;

    if (
      expandedCategory === SearchEntityType.Legislation &&
      legislationHasMore &&
      !isLoadingLegislation
    ) {
      setIsLoadingModalMore(true);
      setModalLegislationPage((prev) => prev + 1);
    } else if (
      expandedCategory === SearchEntityType.WorkspaceDocument &&
      documentHasMore &&
      !isLoadingDocument
    ) {
      setIsLoadingModalMore(true);
      setModalDocumentPage((prev) => prev + 1);
    }
  }, [
    expandedCategory,
    isLoadingModalMore,
    legislationHasMore,
    isLoadingLegislation,
    documentHasMore,
    isLoadingDocument,
  ]);

  // Accumulate legislation results when modal page changes
  useEffect(() => {
    if (!legislationResults) return;

    if (modalLegislationPage === 1) {
      // First page - replace results
      setAccumulatedLegislationResults(legislationResults);
    } else {
      // Subsequent pages - append results
      setAccumulatedLegislationResults((prev) => [
        ...prev,
        ...legislationResults,
      ]);
    }
    setIsLoadingModalMore(false);
  }, [legislationResults, modalLegislationPage]);

  // Accumulate document results when modal page changes
  useEffect(() => {
    if (!documentResults) return;

    if (modalDocumentPage === 1) {
      // First page - replace results
      setAccumulatedDocumentResults(documentResults);
    } else {
      // Subsequent pages - append results
      setAccumulatedDocumentResults((prev) => [...prev, ...documentResults]);
    }
    setIsLoadingModalMore(false);
  }, [documentResults, modalDocumentPage]);

  // For individual entity tabs - combine accumulated results
  const combinedTabResults: SearchPagination<SearchResult> | undefined =
    useMemo(() => {
      if (currentTab === GlobalSearchTab.All) {
        return undefined;
      }

      // If we have accumulated results, always show them (even during loading)
      if (tabResults.length > 0) {
        // If we have fresh data from the API, use its pagination info
        if (singleTabData) {
          return {
            data: tabResults,
            entityPaginationInfo: singleTabData.entityPaginationInfo,
            totalRecords: singleTabData.totalRecords,
          };
        }
        // During loading, use last known pagination info to preserve hasMore state
        if (lastPaginationInfo) {
          return {
            data: tabResults,
            entityPaginationInfo: lastPaginationInfo.entityPaginationInfo,
            totalRecords: lastPaginationInfo.totalRecords,
          };
        }
        // Fallback if no pagination info is available yet
        return {
          data: tabResults,
          entityPaginationInfo: {},
          totalRecords: tabResults.length,
        };
      }

      // First page with no accumulated results yet - return fresh data
      return singleTabData;
    }, [currentTab, singleTabData, tabResults, lastPaginationInfo]);

  // Create a debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string, tab: GlobalSearchTab) => {
      setSearchQuery({
        searchTerm: value,
        searchTags: searchQuery?.searchTags,
        tab: tab,
        filters: searchQuery?.filters,
        workspaceDocumentFilters: searchQuery?.workspaceDocumentFilters,
      });

      // Reset pagination when search changes
      setTabResults([]);
      setTabPage(1);
    }, 300),
    [searchQuery]
  );

  // Update results when new data comes in for individual tabs
  useEffect(() => {
    if (currentTab === GlobalSearchTab.All || !singleTabData) {
      return;
    }

    if (singleTabData.data) {
      setTabResults((prev) => {
        // If page 1, replace results
        if (tabPage === 1) {
          return singleTabData.data;
        }
        // Otherwise, append
        return [...prev, ...singleTabData.data];
      });

      // Store pagination info for use during loading states
      setLastPaginationInfo({
        entityPaginationInfo: singleTabData.entityPaginationInfo,
        totalRecords: singleTabData.totalRecords,
      });

      // Update hasMore based on current tab
      const entityKey = currentTab.toLowerCase();
      const entityInfo = singleTabData.entityPaginationInfo?.[entityKey];
      setHasMoreData(entityInfo?.hasMore || false);

      setIsLoadingMore(false);
    }
  }, [singleTabData, currentTab, tabPage]);

  // Reset tab data when search parameters change or when switching tabs
  useEffect(() => {
    setTabResults([]);
    setTabPage(1);
    setHasMoreData(false);
    setIsLoadingMore(false);
    setLastPaginationInfo(null);
  }, [
    searchQuery?.searchTerm,
    searchQuery?.tab,
    searchQuery?.filters,
    searchQuery?.workspaceDocumentFilters,
  ]);

  // Reset modal pagination and accumulated results when search parameters change
  useEffect(() => {
    setModalLegislationPage(1);
    setModalDocumentPage(1);
    setIsLoadingModalMore(false);
    setAccumulatedLegislationResults([]);
    setAccumulatedDocumentResults([]);
  }, [
    searchQuery?.searchTerm,
    searchQuery?.searchTags,
    searchQuery?.filters,
    searchQuery?.workspaceDocumentFilters,
  ]);

  // Reset modal pagination and accumulated results when going back to overview mode
  useEffect(() => {
    if (expandedCategory === null) {
      setModalLegislationPage(1);
      setModalDocumentPage(1);
      setIsLoadingModalMore(false);
      setAccumulatedLegislationResults([]);
      setAccumulatedDocumentResults([]);
    }
  }, [expandedCategory]);

  // Wrapper for setSources that also updates searchQuery
  const setSourcesAndUpdateQuery = useCallback(
    (newSources: string[]) => {
      isUpdatingSourcesRef.current = true;
      setSources(newSources);
      // Update searchQuery.filters.legislation.datasource
      setSearchQuery((prev) => {
        const prevQuery = prev ?? null;
        const prevFilters = prevQuery?.filters ?? {};
        const newFilters = { ...prevFilters };

        if (newSources.length > 0) {
          newFilters[LEGISLATION_SOURCE_FILTER_KEY] = newSources;
        } else {
          delete newFilters[LEGISLATION_SOURCE_FILTER_KEY];
        }

        return {
          ...(prevQuery || {}),
          filters: newFilters,
        } as Search;
      });
    },
    [setSearchQuery]
  );

  // Reset hasAppliedDefaultsRef when switching away from Legislation tab
  useEffect(() => {
    const currentTab = searchQuery?.tab || GlobalSearchTab.All;
    if (currentTab !== GlobalSearchTab.Legislation) {
      hasAppliedDefaultsRef.current = false;
    }
  }, [searchQuery?.tab]);

  // Apply defaults when Legislation tab is selected and no filters/sources exist
  useEffect(() => {
    const currentTab = searchQuery?.tab || GlobalSearchTab.All;
    if (
      currentTab !== GlobalSearchTab.Legislation ||
      !defaultsPlan ||
      hasAppliedDefaultsRef.current
    ) {
      return;
    }

    // Get current filters
    const currentFilters = searchQuery?.filters || {};

    // Check if user already has selections
    if (sources.length > 0 || Object.keys(currentFilters).length > 0) {
      // User has made selections, mark as initialized without applying defaults
      if (!hasInitializedDefaults) {
        markDefaultsInitialized();
      }
      hasAppliedDefaultsRef.current = true;
      return;
    }

    // User has no selections - apply defaults only if sessionStorage is empty (first session load)
    // After first init, defaults are never applied again, even if filters are cleared
    const { filters: initializedFilters, sources: initializedSources } =
      initializeDefaults(currentFilters, sources, defaultsPlan);

    // Only update if something actually changed
    if (
      JSON.stringify(initializedFilters) !== JSON.stringify(currentFilters) ||
      JSON.stringify(initializedSources) !== JSON.stringify(sources)
    ) {
      // Update sources (this will also update searchQuery via setSourcesAndUpdateQuery)
      setSourcesAndUpdateQuery(initializedSources);

      // Update filters via updateFilter (namespace keys with the source)
      Object.entries(initializedFilters).forEach(([key, values]) => {
        // Keys from initializeDefaults are raw; namespace them with the source
        const namespacedKey =
          key === LEGISLATION_SOURCE_FILTER_KEY
            ? key
            : `${defaultsPlan.sourceKey}::${key}`;
        updateFilter(namespacedKey, values);
      });

      markDefaultsInitialized();
      hasAppliedDefaultsRef.current = true;
    } else {
      // Even if filters didn't change, mark as applied to prevent retrying
      hasAppliedDefaultsRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchQuery?.tab,
    defaultsPlan,
    hasInitializedDefaults,
    initializeDefaults,
    markDefaultsInitialized,
    sources,
    updateFilter,
    setSourcesAndUpdateQuery,
  ]);

  // Parse URL params into state - only on initial mount or when returning to search page
  useEffect(() => {
    const isOnSearchPage = location.pathname === AppRoutes.tenantSearch;
    const currentUrlParams = searchParams.toString();

    if (
      updateUrl &&
      isOnSearchPage &&
      currentUrlParams &&
      currentUrlParams !== lastUrlParamsRef.current
    ) {
      // Use the utility function to parse params
      const urlString = `${location.pathname}?${currentUrlParams}`;
      const parsedSearch = parseSearchUrlParams(urlString);

      setInputValue(parsedSearch.searchTerm);
      setSearchQuery(parsedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, initialTab, updateUrl, location.pathname]);

  // Update URL when search state changes - only if updateUrl is true AND on search page
  useEffect(() => {
    const isOnSearchPage = location.pathname === AppRoutes.tenantSearch;

    if (updateUrl && searchQuery && isOnSearchPage) {
      const newParams = buildSearchUrlParams(searchQuery);
      const newParamsString = newParams.toString();

      // Only update if params actually changed to prevent infinite loop
      if (lastUrlParamsRef.current !== newParamsString) {
        lastUrlParamsRef.current = newParamsString;
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [searchQuery, setSearchParams, updateUrl, location.pathname]);

  // Clean up search params when leaving the search page
  useEffect(() => {
    const isOnSearchPage = location.pathname === AppRoutes.tenantSearch;
    const wasOnSearchPage =
      previousPathnameRef.current === AppRoutes.tenantSearch;

    // If we just left the search page, clear search params
    if (wasOnSearchPage && !isOnSearchPage) {
      const hasSearchParams = Array.from(searchParams.keys()).length > 0;
      if (hasSearchParams) {
        setSearchParams({}, { replace: true });
      }
    }

    // Update the ref for next render
    previousPathnameRef.current = location.pathname;
  }, [location.pathname, searchParams, setSearchParams]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Empty the search field once a search is done, so the next one starts blank.
  // Cancels the pending debounce first, or a keystroke from the search just
  // closed would land in the store afterwards and refill the field.
  const clearSearchField = useCallback(() => {
    debouncedSearch.cancel();
    setInputValue("");
    setSearchQuery(withClearedSearchText);
  }, [debouncedSearch, setSearchQuery]);

  useClearSearchAfterClose({ isSearchbarOpen, clearSearch: clearSearchField });

  // Reset expanded category when search term changes
  useEffect(() => {
    setExpandedCategory(null);
  }, [searchQuery?.searchTerm]);

  // Sync inputValue when modal opens with a pre-populated search (e.g., from shared link)
  // This handles the case where searchQuery was updated in the store externally
  useEffect(() => {
    if (
      isSearchbarOpen &&
      searchQuery?.searchTerm &&
      inputValue !== searchQuery.searchTerm
    ) {
      setInputValue(searchQuery.searchTerm);
    }
    // Only run when modal opens, not on every inputValue change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchbarOpen]);

  // Compute the global search view mode
  const globalSearchViewMode: GlobalSearchViewMode = useMemo(() => {
    const hasSearchTerm =
      searchQuery?.searchTerm?.trim() ||
      (searchQuery?.searchTags && searchQuery.searchTags.length > 0);

    if (!hasSearchTerm) {
      return "initial";
    }
    if (expandedCategory !== null) {
      return "expanded";
    }
    return "results";
  }, [searchQuery?.searchTerm, searchQuery?.searchTags, expandedCategory]);

  const value = {
    inputValue,
    setInputValue,
    debouncedSearch,

    // For "All" tab - 4 separate result sets
    legislationResults,
    workspaceResults,
    documentResults,
    chatResults,

    // Loading states per entity (for "All" tab)
    isLoadingLegislation,
    isLoadingWorkspace,
    isLoadingDocument,
    isLoadingChat,

    // HasMore flags per entity (for "All" tab)
    legislationHasMore,
    workspaceHasMore,
    documentHasMore,
    chatHasMore,

    // Total records per entity (for displaying actual counts)
    legislationTotalRecords,
    documentTotalRecords,

    // Modal infinite scroll support
    loadMoreModalResults,
    isLoadingModalMore,
    accumulatedLegislationResults: accumulatedLegislationResults.filter(
      (r): r is NonNullable<typeof r> => !!r
    ),
    accumulatedDocumentResults: accumulatedDocumentResults.filter(
      (r): r is NonNullable<typeof r> => !!r
    ),

    // Per-source counts for legislation searches
    sourceCounts,

    // Focused source state
    focusedSource,
    setFocusedSource,

    // For individual entity tabs - unified results with infinite scroll
    searchResults: combinedTabResults,
    isLoading: isLoadingSingleTab,
    isLoadingMore,
    hasMoreData,

    leveledFilters,
    defaultsPlan,
    sources,
    setSources: setSourcesAndUpdateQuery,
    updateFilter,
    loadMoreResults,
    updateUrl,
    setUpdateUrl,
    searchQuery,
    setSearchQuery,
    setIsSearchbarOpen,
    setIsFilterOpen,
    isSearchbarOpen,
    isFilterOpen,

    // New global search modal state
    expandedCategory,
    setExpandedCategory,
    globalSearchViewMode,
  };

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
    </GlobalSearchContext.Provider>
  );
};
